import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { supabase } from '../lib/supabase'
import { useAuthStore } from './authStore'
import { Equipment } from '../../types'

interface Category {
  id: string
  name: string
  description: string | null
  order_index: number | null
  created_at: string
  updated_at: string
}

interface SupabaseEquipmentStore {
  equipment: Equipment[]
  categories: Category[]
  loading: boolean
  error: string | null
  
  // Equipment Actions
  fetchEquipment: () => Promise<void>
  addEquipment: (equipment: Omit<Equipment, 'id'>) => Promise<{ error?: string }>
  updateEquipment: (id: string, updates: Partial<Equipment>) => Promise<{ error?: string }>
  deleteEquipment: (id: string) => Promise<{ error?: string }>
  uploadEquipmentImage: (file: File) => Promise<{ url?: string, error?: string }>
  
  // Categories Actions
  fetchCategories: () => Promise<void>
  addCategory: (category: Omit<Category, 'id' | 'created_at' | 'updated_at'>) => Promise<{ error?: string }>
  updateCategory: (id: string, updates: Partial<Category>) => Promise<{ error?: string }>
  deleteCategory: (id: string) => Promise<{ error?: string }>
  
  // Helpers
  getEquipmentByCategory: (category: string) => Equipment[]
  getAllCategories: () => string[]
}

export const useSupabaseEquipmentStore = create<SupabaseEquipmentStore>()(
  persist(
    (set, get) => ({
      equipment: [],
      categories: [],
      loading: false,
      error: null,

      fetchEquipment: async () => {
        set({ loading: true, error: null })
        try {
          const { data, error } = await supabase
            .from('equipment')
            .select('*')
            .order('category', { ascending: true })
            .order('name', { ascending: true })

          if (error) throw error

          // Map database columns (snake_case, nullable) to the Equipment domain type
          const mappedData: Equipment[] = (data || []).map(item => ({
            id: item.id,
            name: item.name,
            description: item.description,
            category: item.category,
            price: item.price ?? undefined,
            sizes: item.sizes ?? undefined,
            colors: item.colors ?? undefined,
            image: item.image ?? undefined,
            inStock: item.in_stock !== false,
          }))

          set({ equipment: mappedData, loading: false })
        } catch (error: any) {
          set({ error: error.message, loading: false })
        }
      },

      addEquipment: async (equipment) => {
        try {
          const { profile } = useAuthStore.getState()
          if (!profile || profile.role !== 'admin') {
            return { error: 'Nur Administratoren können Equipment hinzufügen' }
          }

          // Map inStock to in_stock for database (destructure so `inStock`
          // is actually gone from the type, not just deleted at runtime)
          const { inStock, ...rest } = equipment
          const dbEquipment = {
            ...rest,
            in_stock: inStock !== false
          }

          const { error } = await supabase
            .from('equipment')
            .insert(dbEquipment)

          if (error) throw error

          // Equipment neu laden
          get().fetchEquipment()
          return {}
        } catch (error: any) {
          return { error: error.message }
        }
      },

      updateEquipment: async (id, updates) => {
        try {
          const { profile } = useAuthStore.getState()
          if (!profile || profile.role !== 'admin') {
            return { error: 'Nur Administratoren können Equipment bearbeiten' }
          }

          // Map inStock to in_stock for database (destructure so `inStock`
          // is actually gone from the type, not just deleted at runtime)
          const { inStock, ...dbUpdates } = updates
          const finalUpdates = inStock === undefined
            ? dbUpdates
            : { ...dbUpdates, in_stock: inStock !== false }

          const { error } = await supabase
            .from('equipment')
            .update(finalUpdates)
            .eq('id', id)

          if (error) throw error

          // Equipment neu laden
          get().fetchEquipment()
          return {}
        } catch (error: any) {
          return { error: error.message }
        }
      },

      deleteEquipment: async (id) => {
        try {
          const { profile } = useAuthStore.getState()
          if (!profile || profile.role !== 'admin') {
            return { error: 'Nur Administratoren können Equipment löschen' }
          }

          const { error } = await supabase
            .from('equipment')
            .delete()
            .eq('id', id)

          if (error) throw error

          // Equipment neu laden
          get().fetchEquipment()
          return {}
        } catch (error: any) {
          return { error: error.message }
        }
      },

      uploadEquipmentImage: async (file) => {
        try {
          const { profile } = useAuthStore.getState()
          if (!profile || profile.role !== 'admin') {
            return { error: 'Nur Administratoren können Bilder hochladen' }
          }

          const fileExt = file.name.split('.').pop()
          const fileName = `equipment/${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`

          const { error: uploadError } = await supabase.storage
            .from('equipment-images')
            .upload(fileName, file)

          if (uploadError) throw uploadError

          const { data: { publicUrl } } = supabase.storage
            .from('equipment-images')
            .getPublicUrl(fileName)

          return { url: publicUrl }
        } catch (error: any) {
          return { error: error.message }
        }
      },

      fetchCategories: async () => {
        try {
          const { data, error } = await supabase
            .from('equipment_categories')
            .select('*')
            .order('order_index', { ascending: true })

          if (error) throw error

          set({ categories: data || [] })
        } catch (error: any) {
          set({ error: error.message })
        }
      },

      addCategory: async (category) => {
        try {
          const { profile } = useAuthStore.getState()
          if (!profile || profile.role !== 'admin') {
            return { error: 'Nur Administratoren können Kategorien hinzufügen' }
          }

          const { error } = await supabase
            .from('equipment_categories')
            .insert(category)

          if (error) throw error

          // Kategorien neu laden
          get().fetchCategories()
          return {}
        } catch (error: any) {
          return { error: error.message }
        }
      },

      updateCategory: async (id, updates) => {
        try {
          const { profile } = useAuthStore.getState()
          if (!profile || profile.role !== 'admin') {
            return { error: 'Nur Administratoren können Kategorien bearbeiten' }
          }

          const { error } = await supabase
            .from('equipment_categories')
            .update(updates)
            .eq('id', id)

          if (error) throw error

          // Kategorien neu laden
          get().fetchCategories()
          return {}
        } catch (error: any) {
          return { error: error.message }
        }
      },

      deleteCategory: async (id) => {
        try {
          const { profile } = useAuthStore.getState()
          if (!profile || profile.role !== 'admin') {
            return { error: 'Nur Administratoren können Kategorien löschen' }
          }

          // Prüfen ob Equipment in dieser Kategorie existiert
          const categoryToDelete = get().categories.find(c => c.id === id)
          if (categoryToDelete) {
            const equipmentInCategory = get().equipment.filter(e => e.category === categoryToDelete.name)
            if (equipmentInCategory.length > 0) {
              return { error: 'Kategorie kann nicht gelöscht werden, da noch Equipment zugeordnet ist' }
            }
          }

          const { error } = await supabase
            .from('equipment_categories')
            .delete()
            .eq('id', id)

          if (error) throw error

          // Kategorien neu laden
          get().fetchCategories()
          return {}
        } catch (error: any) {
          return { error: error.message }
        }
      },

      getEquipmentByCategory: (category) => {
        return get().equipment.filter(item => item.category === category)
      },

      getAllCategories: () => {
        // Return categories from the categories table, not from equipment
        return get().categories.map(cat => cat.name).sort()
      },
    }),
    {
      name: 'fc-frick-equipment-supabase',
      partialize: (state) => ({
        equipment: state.equipment,
        categories: state.categories,
      }),
    }
  )
)
